import { QueryFunctionContext } from '@tanstack/react-query';
import { OLD_QBG, PlayingSourceEnums } from 'src/constants/constants';
import { CLIENT_TYPE } from 'src/constants/type';
import { getMediaCapabilities } from 'src/services/CustomMediaCapabilities';
import { logger } from 'src/services/logger/loggerService';
import { getClientId, isPPAdmin, isValidPWDomain } from 'src/services/utils';
import { getQueryParams } from 'src/usePlayerParams';

import { Client } from '../api-client';
import { CookieKeys, CookieService } from '../cookie-service';
import { isOTT } from '../utilities';

export const getScheduleDetailsUrl = (batchId: string, subjectSlug: string, scheduleId: string) =>
  `v1/batches/${batchId}/subject/${subjectSlug}/schedule/${scheduleId}/schedule-details`;

export const getMyRatingUrl = (typeId: string) => `v1/ratings/${typeId}/my-ratings`;

export const getDPPQuizUrl = () => 'v3/test-service/tests/user-test-student-mapping-list';

export const getRatingTagsUrl = () => 'v1/ratings/rating-tags';

export const getAddVideoRatingUrl = () => 'v1/ratings';

export const getSaveStatsUrl = () => 'v3/video-stats/save-stats';

export const getFetchStatsUrl = () => 'v3/video-stats/fetch-stats';

export const getOtpUrl = () => 'v1/videos/get-otp';

export const getSignedCookieUrl = () => 'v1/files/get-signed-cookie';

export const subscribeToNotifUrl = () => 'v1/notification/subscribe-topic';

export const getVideoReportTagsUrl = () => `v1/organizations/${getClientId()}/video-report-tags`;

export const getVideoReportUrl = () => 'v1/enquiry/public-enquiry-ticket';

const getReportTags = (organizationId: string) => `v1/organizations/${organizationId}/report-tags`;

export const registerVideoSesson = () => 'uxncc-be-go/video-stats/v1/register-session';

export const syncVideoStats = () => 'uxncc-be-go/video-stats/v1/sync-stats';

export const getVideoUrlDetails = () => 'v1/videos/video-url-details';

export const getAdminVideoUrlDetails = () => 'v1/admin/videos/video-url-details';

export const getClientVideoUrlDetails = () => 'media/videos/video-url-details';

export const getQBGClientVideoUrlDetails = () => 'qbg/qbg-pp/media/videos/video-url-details';

export const scheduleDetailsFetcher = ({ queryKey }: any) => {
  const { batchId, subjectSlug, scheduleId } = queryKey[1];
  return Client.get(getScheduleDetailsUrl(batchId, subjectSlug, scheduleId), {
    headers: { 'secure-video-required': isValidPWDomain() },
  });
};

export const DPPQuizStateFetcher = (data: any) => {
  const { ...payload } = data;
  return Client.post(getDPPQuizUrl(), { ...payload }).then((res: any) => res?.data);
};

export const getAdminScheduleDetailsUrl = (scheduleId: string) =>
  `${process.env.REACT_APP_PP_ADMIN_API_BACKEND}/v1/admin/batches/schedule/${scheduleId}`;

type AdminOptionQueryKey = [string, { scheduleId: string; batchId: string; subjectSlug: string }];
export const adminScheduleDetailsFetcher = ({
  queryKey,
}: QueryFunctionContext<AdminOptionQueryKey>) => {
  const { scheduleId } = queryKey[1];
  return Client.get(getAdminScheduleDetailsUrl(scheduleId), { endPointWithBaseUrl: true });
};

export const getMyRatingsFetcher = ({ queryKey }: any) => {
  const { typeId } = queryKey[1];
  return Client.get(getMyRatingUrl(typeId));
};

export const getRatingTagsFetch = ({ queryKey }: any) => {
  return Client.get(getRatingTagsUrl());
};

export const subscribeToNotif = (payload: any) => {
  return Client.post(subscribeToNotifUrl(), payload).then((res: any) => res?.data);
};

export const addVideoRatingFetcher = (payload: any) => {
  return Client.post(getAddVideoRatingUrl(), payload).then((res: any) => res?.data);
};

export const saveStatsFetcher = ({ queryKey }: any) => {
  const { saveStatsData } = queryKey[1];

  return Client.post(getSaveStatsUrl(), saveStatsData);
};

export const fetchStatsFetcher = ({ queryKey }: any) => {
  const { typeId, userId } = queryKey[1];
  const params = {
    typeId,
    userId,
  };

  return Client.get(getFetchStatsUrl(), { params });
};

export const getOtpFetcher = ({ queryKey }: any) => {
  const { key } = queryKey[1];

  CookieService.setValue(CookieKeys.TOKEN_CONTEXT, '');
  const params = {
    key,
  };

  return Client.get(getOtpUrl(), { params });
};

export const getSignedCookieFetcher = ({ queryKey }: any) => {
  const { url } = queryKey[1];

  const params = {
    url,
  };

  return Client.post(getSignedCookieUrl(), params);
};

export const getVideoReportTagsFetcher = ({ queryKey }: any) => {
  return Client.get(getVideoReportTagsUrl());
};

export const reportVideoFetcher = (data: any) => {
  return Client.post(getVideoReportUrl(), data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getReportTagsFetcher = ({ queryKey }: any) => {
  const { organizationId, module, tag } = queryKey[1];
  const params = {
    module: module,
    tag: tag,
  };

  return Client.get(getReportTags(organizationId), {
    params,
  });
};

export const registerVideoSessionFetcher = (data: any) => {
  const isOTTMode = isOTT();
  const url = isOTTMode ? `${registerVideoSesson()}?source=ott` : registerVideoSesson();
  return Client.post(url, data);
};

export const syncVideoStatsFetcher = (data: any) => {
  return Client.post(syncVideoStats(), data);
};

export const getVideoUrlDetailsFetcher = async ({ queryKey }: any) => {
  const {
    type,
    childId,
    parentId,
    secondaryChildId,
    secondaryParentId,
    videoId,
    videoUrl,
    disableCMAF,
    skillsToken,
    videoContainerType,
    metadata,
    isFromCjr,
  } = queryKey[1];

  const params: any = {
    type,
    videoContainerType,
    reqType: 'query',
    ...(childId && { childId: childId }),
    ...(parentId && { parentId: parentId }),
    ...(videoId && { videoId: videoId }),
    ...(skillsToken && { skillToken: skillsToken }),
    ...(videoUrl && ![PlayingSourceEnums.SKILLS].includes(type) && { videoUrl: videoUrl }),
    ...(secondaryChildId && { secondaryChildId: secondaryChildId }),
    ...(secondaryParentId && { secondaryParentId: secondaryParentId }),
    ...(disableCMAF && { isCmaf: false }),
    ...(metadata && isFromCjr && { metadata: metadata }),
    clientVersion: '201',
  };

  let mediaCapabilities: any;

  try {
    mediaCapabilities = await getMediaCapabilities();
  } catch (error) {
    logger.error('Error fetching media capabilities:', error);
    mediaCapabilities = {};
  } finally {
    console.log('Media capabilities fetch attempt finished.');
  }

  return Client.get(getVideoUrlDetails(), {
    params,
    headers: mediaCapabilities,
  });
};

const getVideoUrlDetailsEndpoint = () => {
  const { vType, distinctBaseURL } = getQueryParams();

  const resolveKey = (): string => {
    if (distinctBaseURL) return distinctBaseURL.toLowerCase();
    if (isPPAdmin()) return CLIENT_TYPE.ADMIN;
    if (vType === OLD_QBG) return OLD_QBG.toLowerCase();
    return '';
  };
  const key = resolveKey();
  const endpointMapping: Record<string, string | undefined> = {
    erp: `${process.env.REACT_APP_ERP_API_BACKEND}${getClientVideoUrlDetails()}`,
    admin: `${process.env.REACT_APP_PP_ADMIN_API_BACKEND}${getAdminVideoUrlDetails()}`,
    old_qbg: `${process.env.REACT_APP_PP_API_BACKEND}${getQBGClientVideoUrlDetails()}`,
  };
  return endpointMapping[key] || '';
};

export const getAdminVideoUrlDetailsFetcher = async ({ queryKey }: any) => {
  const { vType, distinctBaseURL, videoSrc, erpToken } = getQueryParams();

  const {
    type,
    childId,
    parentId,
    secondaryChildId,
    secondaryParentId,
    videoId,
    videoUrl,
    videoContainerType,
  } = queryKey[1];

  const params: any = {
    type,
    videoContainerType,
    reqType: 'query',
    ...(childId && { childId: childId }),
    ...(parentId && { parentId: parentId }),
    ...(videoId && { videoId: videoId }),
    ...(videoUrl && { videoUrl: videoUrl }),
    ...(videoSrc && { videoUrl: videoSrc }),
    ...(secondaryChildId && { secondaryChildId: secondaryChildId }),
    ...(secondaryParentId && { secondaryParentId: secondaryParentId }),
    clientVersion: '201',
  };

  let mediaCapabilities: any;

  try {
    mediaCapabilities = await getMediaCapabilities();
  } catch (error) {
    logger.error('Error fetching media capabilities:', error);
    mediaCapabilities = {}; // Fallback in case of an error
  } finally {
    console.log('Media capabilities fetch attempt finished.');
  }

  return Client.get(getVideoUrlDetailsEndpoint(), {
    params,
    headers: {
      ...mediaCapabilities,
      organizationId: getClientId(),
      'client-type': 'ADMIN',
      ...(distinctBaseURL?.toLowerCase() === CLIENT_TYPE.ERP && { Authorization: erpToken }),
      ...(vType === OLD_QBG && { roles: CLIENT_TYPE.QBG_ADMIN }),
    },
    ...((distinctBaseURL?.toLowerCase() === CLIENT_TYPE.ERP ||
      isPPAdmin() ||
      vType === OLD_QBG) && {
      endPointWithBaseUrl: true,
    }),
  });
};

export const trackConsumeFetcher = async ({
  cohortId,
  grade,
  trackId,
  trackType,
  progressInPercentage,
  utm_source,
  utm_medium,
  utm_campaign,
  utm_term,
  utm_content,
  came_from,
}: {
  cohortId: string;
  grade: string;
  trackId: string;
  trackType: string;
  progressInPercentage: number;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  came_from?: string;
}) => {
  const queryParams: Record<string, string> = {};

  if (utm_source) queryParams.utm_source = utm_source;
  if (utm_medium) queryParams.utm_medium = utm_medium;
  if (utm_campaign) queryParams.utm_campaign = utm_campaign;
  if (utm_term) queryParams.utm_term = utm_term;
  if (utm_content) queryParams.utm_content = utm_content;
  if (came_from) queryParams.came_from = came_from;

  return Client.post(
    `${process.env.REACT_APP_CJR_API_BACKEND}v1/journey/track/consume`,
    {
      cohortId,
      grade,
      trackId,
      trackType,
      progressInPercentage,
    },
    {
      endPointWithBaseUrl: true,
      params: queryParams,
    },
  );
};
